// src/util/navStateUtils.js
// Section expansion state management

const STORAGE_KEY = 'nav_section_state';

/**
 * Get the localStorage key for section state
 * @param {string} companyId
 * @param {string} userRole
 * @returns {string}
 */
function getStorageKey(companyId, userRole) {
  return `${STORAGE_KEY}_${companyId}_${userRole || 'default'}`;
}

/**
 * Get default expansion state
 * All sections expanded by default for initial launch
 */
export function getDefaultExpansionState() {
  return {
    work: true,
    completed: true,
    reference: true,
    system: true
  };
}

/**
 * Load section expansion state from localStorage
 * @param {string} companyId
 * @param {string} userRole
 * @returns {object} Section expansion state
 */
export function loadExpansionState(companyId, userRole) {
  try {
    const key = getStorageKey(companyId, userRole);
    const stored = localStorage.getItem(key);

    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.error('Failed to load nav expansion state:', e);
  }

  return getDefaultExpansionState();
}

/**
 * Save section expansion state to localStorage
 * @param {string} companyId
 * @param {string} userRole
 * @param {object} state - Section expansion state
 */
export function saveExpansionState(companyId, userRole, state) {
  try {
    const key = getStorageKey(companyId, userRole);
    localStorage.setItem(key, JSON.stringify(state));
  } catch (e) {
    console.error('Failed to save nav expansion state:', e);
  }
}

/**
 * Clear expansion state for a company (used on company switch)
 * @param {string} companyId
 */
export function clearExpansionStateForCompany(companyId) {
  try {
    const keys = Object.keys(localStorage).filter(
      key => key.startsWith(`${STORAGE_KEY}_${companyId}_`)
    );
    keys.forEach(key => localStorage.removeItem(key));
  } catch (e) {
    console.error('Failed to clear nav expansion state:', e);
  }
}

// --- Sidebar collapsed state ---

const SIDEBAR_KEY = 'nav_sidebar_collapsed';

export function loadSidebarCollapsed(companyId) {
  try {
    const stored = localStorage.getItem(`${SIDEBAR_KEY}_${companyId}`);
    return stored === 'true';
  } catch (e) {
    return false;
  }
}

export function saveSidebarCollapsed(companyId, collapsed) {
  try {
    localStorage.setItem(`${SIDEBAR_KEY}_${companyId}`, String(collapsed));
  } catch (e) {
    // silent
  }
}
